import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Container, Stack, Text, Title, Button, Paper, Loader, Center } from '@mantine/core'
import { IconCheck, IconPlayerPlay } from '@tabler/icons-react'
import { paymentService } from '../services/paymentService'

export default function EnrollmentSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const sessionId = searchParams.get('session_id')

  const { data, isLoading, error } = useQuery({
    queryKey: ['paymentVerify', sessionId],
    queryFn: () => paymentService.verifyPayment(sessionId),
    enabled: !!sessionId,
    retry: 3,
    retryDelay: 2000,
  })

  // Invalidate story queries so the catalog picks up newly accessible stories
  useEffect(() => {
    if (data?.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['stories'] })
      queryClient.invalidateQueries({ queryKey: ['enrollment'] })
    }
  }, [data?.status, queryClient])

  if (!sessionId) {
    return (
      <Center py="xl" style={{ minHeight: '60vh' }}>
        <Text c="dimmed">Invalid session. No payment session found.</Text>
      </Center>
    )
  }

  if (isLoading) {
    return (
      <Center py="xl" style={{ minHeight: '60vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" color="cyan" />
          <Text c="dimmed">Verifying your payment...</Text>
        </Stack>
      </Center>
    )
  }

  if (error || data?.status === 'failed') {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" className="glass">
          <Stack align="center" gap="md">
            <Title order={3} ff="Orbitron, sans-serif" c="red.4">
              Payment Issue
            </Title>
            <Text c="dimmed" ta="center">
              There was an issue verifying your payment. If you were charged,
              your access will be activated shortly. Please contact support if
              the issue persists.
            </Text>
            <Button variant="light" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </Stack>
        </Paper>
      </Container>
    )
  }

  return (
    <Container size="sm" py="xl">
      <Paper p="xl" className="glass">
        <Stack align="center" gap="lg">
          <IconCheck size={48} color="var(--mantine-color-green-5)" />
          <Title order={2} ff="Orbitron, sans-serif" c="cyan.4" ta="center">
            Enrollment Complete
          </Title>
          <Text c="dimmed" ta="center" size="lg">
            You now have access to all stories in this course.
          </Text>
          <Button
            size="lg"
            leftSection={<IconPlayerPlay size={20} />}
            onClick={() => navigate('/')}
            className="glow-cyan"
          >
            Start Exploring
          </Button>
        </Stack>
      </Paper>
    </Container>
  )
}
